import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react'
import { onAuthStateChanged, signInWithPopup, signInWithRedirect, GoogleAuthProvider, signOut } from 'firebase/auth'
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore'
import { auth, db } from '../config/firebase'
import { ENTITLEMENT_COLLECTION, daysRemaining, entitlementActive, hasPremium, lifetimeTeacherAccess } from '../lib/access'
import { fetchTrialStatus } from '../lib/trial'

const AuthContext = createContext(null)

// Tester whitelist — these emails get full premium access
const TESTER_EMAILS = [
  'semereabweldegebriel@gmail.com',
  'ybrussom@yahoo.com',
  'ydroberts@gmail.com',
  'matiasnegassi@gmail.com',
  'endaleashagrie@yahoo.com',
  'kahsayteame76@gmail.com', // Deacon — timestamp team
]

// Admin whitelist — these emails get access to the admin dashboard
const ADMIN_EMAILS = [
  'ybrussom@yahoo.com',
  'ydroberts@gmail.com',
]

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [subscription, setSubscription] = useState(null)
  const [entitlement, setEntitlement] = useState(null)
  // Authoritative trial state from the server. Null until it has been asked,
  // so the UI can stay quiet rather than offering a trial it may not be able
  // to grant.
  const [trialStatus, setTrialStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const unsubFirestoreRef = useRef(null)
  const unsubEntitlementRef = useRef(null)

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser)

      // Clean up previous Firestore listeners
      if (unsubFirestoreRef.current) {
        unsubFirestoreRef.current()
        unsubFirestoreRef.current = null
      }
      if (unsubEntitlementRef.current) {
        unsubEntitlementRef.current()
        unsubEntitlementRef.current = null
      }
      setEntitlement(null)

      if (firebaseUser) {
        const userRef = doc(db, 'users', firebaseUser.uid)
        const userSnap = await getDoc(userRef)

        if (!userSnap.exists()) {
          await setDoc(userRef, {
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
            createdAt: new Date().toISOString(),
            subscriptionStatus: 'free'
          })
        }

        // Real-time listener for universal subscription status
        unsubFirestoreRef.current = onSnapshot(userRef, (snapshot) => {
          const data = snapshot.data()
          const sub = data?.subscription
          setSubscription({
            status: sub?.status || 'free',
            stripeCustomerId: sub?.stripeCustomerId || null,
            currentPeriodEnd: sub?.currentPeriodEnd || null
          })
          setLoading(false)
        })

        // Geezword account entitlement (Kidase Individual bought on account.geezword.com)
        unsubEntitlementRef.current = onSnapshot(
          doc(db, ENTITLEMENT_COLLECTION, firebaseUser.uid),
          (snapshot) => setEntitlement(snapshot.data() || null),
          () => setEntitlement(null)
        )
      } else {
        setSubscription(null)
        setLoading(false)
      }
    })

    return () => {
      unsubAuth()
      if (unsubFirestoreRef.current) unsubFirestoreRef.current()
      if (unsubEntitlementRef.current) unsubEntitlementRef.current()
    }
  }, [])

  // Try popup first; if blocked, fall back to redirect
  const login = async () => {
    const provider = new GoogleAuthProvider()
    provider.setCustomParameters({ prompt: 'select_account' })
    try {
      return await signInWithPopup(auth, provider)
    } catch (err) {
      // A second sign-in click while a Google window is already open cancels the
      // first request; the newer window carries on, so this is not a failure.
      if (err.code === 'auth/cancelled-popup-request') return
      if (err.code === 'auth/popup-blocked' || err.code === 'auth/popup-closed-by-user') {
        return signInWithRedirect(auth, provider)
      }
      console.error('Sign-in error:', err)
      if (err.code === 'auth/unauthorized-domain') {
        alert(`This domain is not authorized for sign-in. Please add "${window.location.hostname}" to Firebase Auth authorized domains.`)
      } else {
        alert(`Sign-in failed. Please try again. (${err.code || 'unknown error'})`)
      }
    }
  }

  // Asked once per signed-in account. An entitlement cannot answer this: it
  // says nothing about a trial that has already ended, and "already used" must
  // look different from "never had one".
  const loadTrialStatus = useCallback(async () => {
    if (!user?.emailVerified) { setTrialStatus(null); return null }
    try {
      const status = await fetchTrialStatus()
      setTrialStatus(status)
      return status
    } catch {
      // A failed lookup must not break the app: the trial controls simply stay
      // hidden until it succeeds.
      setTrialStatus(null)
      return null
    }
  }, [user])

  useEffect(() => { loadTrialStatus() }, [loadTrialStatus])

  const logout = () => signOut(auth)
  const isTester = Boolean(user?.email && TESTER_EMAILS.includes(user.email.toLowerCase()))
  const isPremium = hasPremium({ isTester, subscription, entitlement })
  // Where "Manage" goes: the Geezword account page for account subscriptions,
  // the existing Stripe portal link for legacy Kidase subscriptions.
  const hasAccountSubscription = entitlementActive(entitlement)
  const hasLifetimeTeacherAccess = lifetimeTeacherAccess(entitlement)
  const isAdmin = user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase())
  // Days left on whatever dated access this account has. A lifetime teacher
  // grant has no end date, so this is null for them.
  const premiumDaysRemaining = daysRemaining(entitlement)
  // Offer the trial only to a verified account the server says is eligible,
  // and never to someone who already has Premium by any route.
  const canStartTrial = Boolean(user?.emailVerified && trialStatus?.eligible && !isPremium)
  const trialUsed = Boolean(trialStatus?.used)
  const onFreeTrial = Boolean(trialStatus?.reason === 'active' && isPremium)

  return (
    <AuthContext.Provider value={{ user, subscription, loading, login, logout, isPremium, hasAccountSubscription, hasLifetimeTeacherAccess, isTester, isAdmin,
      trialStatus, canStartTrial, trialUsed, onFreeTrial, premiumDaysRemaining, refreshTrialStatus: loadTrialStatus }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within AuthProvider')
  return context
}
